import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
} from 'react'
import { useStore, type Toast } from '../core/store'
import { useMedia } from './components/useMedia'

/**
 * Toasts.
 *
 * Key entry used to live here too. It moved to `Setup.tsx` and is not wrapped or
 * re-exported: two surfaces that can set a key are two surfaces that can disagree
 * about which provider it belongs to.
 */

/** The ink variants, not the raw channel hues: a toast is a LINE OF TEXT in its
 *  tint, not a dot beside neutral text, so the tint has to clear text contrast
 *  on both fields. */
const TOAST_TINT: Record<string, string> = {
  info: 'var(--accent)',
  good: 'var(--ink-insight)',
  warn: 'var(--ink-gap)',
  bad: 'var(--ink-tension)',
}

/** Where the stack rests with nothing under it: above the transport in the
 *  field's lower corner, where the ledger dock stands. */
const TOAST_REST = 118

/** The daylight the stack keeps above the bottom band it stands over. */
const TOAST_GAP = 10

/**
 * How far above the stage's bottom edge the stack stands: its resting 118, or
 * clear of the whole bottom band, whichever is higher.
 *
 * The band is the one the camera frames above (`viewInset.bottom`, measured by
 * `canvas/fieldInset.ts`): the floating composer's column from its top edge
 * down — the advisory lines over the input ("That reads like a question…",
 * "33 words — 30 or more reads as…"), the chips, the thought line and the
 * transport included — and on a phone the node sheet. A guessed 118 put
 * "Add and argue with it"'s toasts across exactly those lines, on the blank
 * canvas where the composer floats and the lifted claim had just raised them.
 * With the composer in the thread's column the band is empty, and the stack
 * rests where it always has.
 */
export function toastBottom(band: number): number {
  return Math.max(TOAST_REST, Math.round(band) + TOAST_GAP)
}

/**
 * Under an open sheet on a phone, the top edge instead.
 *
 * The composer is behind the sheet's glass there and the band's top is the
 * middle of the sheet — the sources sheet's paste box, on the session that
 * found this — so the stack goes into the margin a phone's sheet leaves above
 * itself. Asked again with each new toast, which is when it matters.
 */
function usePhoneSheetUp(toasts: readonly unknown[]): boolean {
  const narrow = useMedia('(max-width: 767px)')
  const [up, setUp] = useState(false)
  useLayoutEffect(() => {
    setUp(narrow && toasts.length > 0 && !!document.querySelector('.scrim'))
  }, [narrow, toasts])
  return up
}

/**
 * How soon after a key press a toast with an action counts as that press's
 * answer. The answers this is for arrive in the same tick as the press — the
 * gap's "Add to the standing brief", the footer's Prune — so the window only has
 * to cover a frame or two; a toast later than this is news from elsewhere (a key
 * check that came back, a run ending), and news never takes focus.
 */
const TOAST_ANSWER_MS = 1000

/**
 * Where a keyboard reaches a toast's action, and what that answer is.
 *
 * The stack is the last thing in the document, so its buttons — "Put it back",
 * "Undo", "See the ledger" — were the last stops in the tab order: 32 Tabs from
 * the gap's form to the Undo of the answer it had just added, inside a toast
 * that is gone in 6.5 seconds. ⌘Z is not the route for all of them. It steps
 * back through history, so it is the same act as "Put it back" and the brief's
 * two Undos only while nothing newer sits on top; grouping reports takes no step
 * of history at all, so ⌘Z there undoes something older instead; "See the
 * ledger" and "Paste another" are not undos; and in a field ⌘Z is the field's.
 *
 * So a toast whose action is the answer to the press just made takes focus —
 * but only where that press took focus away with it. The gap's form closing on
 * its Add, the footer's Prune greying under the key once the selection falls to
 * the seed: each unmounted or disabled the button that was pressed, and so left
 * focus on the page body or about to land there, which is D217's failure, and
 * the toast is what replaced it. Four conditions, all read two frames after the
 * toast arrives, when every handoff that runs in the press's own commit has had
 * its turn: the last input was a key, it came within `TOAST_ANSWER_MS`, focus is
 * on nothing (or on a disabled control, D342), and no modal sheet is up (a toast is
 * outside every sheet's trap). A pointer's press never moves focus, and somebody
 * typing has focus in a field, so nobody's caret is ever taken.
 *
 * A toast with focus in it stays until focus leaves it: the store's timer lets
 * it go and this keeps it in place (`useHeldToasts`), because a button that
 * vanished under the reader's focus is the same failure again. Escape on it
 * dismisses it; either way out, focus goes back to where it came from if that is
 * still on the page. The stack is a letter zone (`LETTER_ZONES`), so the verbs'
 * letters act from it as they did from the body it replaced.
 */
function useKeyedAnswers(toasts: readonly Toast[]) {
  const lastInput = useRef<{ key: boolean; at: number }>({ key: false, at: 0 })
  useEffect(() => {
    const key = () => (lastInput.current = { key: true, at: performance.now() })
    const pointer = () => (lastInput.current = { key: false, at: performance.now() })
    window.addEventListener('keydown', key, true)
    window.addEventListener('pointerdown', pointer, true)
    return () => {
      window.removeEventListener('keydown', key, true)
      window.removeEventListener('pointerdown', pointer, true)
    }
  }, [])

  const seen = useRef(new Set(toasts.map((t) => t.id)))
  useEffect(() => {
    const fresh = toasts.filter((t) => !seen.current.has(t.id))
    seen.current = new Set(toasts.map((t) => t.id))
    const newest = fresh.at(-1)
    if (!newest?.action) return
    const arrived = performance.now()
    // Not cancelled when the list changes again: the store letting an older
    // toast go in these two frames must not cost this one its focus. A toast
    // gone by then is simply not found.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const { key, at } = lastInput.current
        if (!key || arrived - at > TOAST_ANSWER_MS) return
        const here = document.activeElement
        // A control its own press disabled still holds focus until the browser's
        // focus fixup, at a later rendering update than these two frames, so it
        // counts as focus on nothing already. The footer's Prune does exactly
        // this: the selection falls to the seed, which cannot be pruned, and the
        // button greys under the key rather than unmounting (D342). A press that
        // must keep focus uses aria-disabled (D217), which `:disabled` never matches.
        if (here && here !== document.body && !here.matches(':disabled')) return
        if (document.querySelector('[aria-modal="true"]')) return
        document
          .querySelector<HTMLElement>(`[data-toast="${newest.id}"] .toast-act`)
          ?.focus({ preventScroll: true })
      }),
    )
  }, [toasts])
}

/**
 * How long a toast the pointer held stays after the pointer leaves its action.
 * Long enough to cover a hand that overshoots and comes back, and a touch tap,
 * whose pointerleave follows its own lift; short enough that a toast nobody is
 * reaching for goes the way it would have.
 */
export const TOAST_POINTER_GRACE_MS = 1200

/**
 * The store's toasts, plus the one focus is in if the store has let it go —
 * kept where it stood, so its element is not moved (a moved element loses
 * focus). `release` lets it go, and hands focus back to where it came from.
 *
 * And the one the POINTER is on (D335). The store lets an actionable toast go
 * after 6.5 seconds whoever is reaching for it, so a hand on its way to "Put it
 * back" could watch the button vanish under it — and the click then landed on
 * the canvas behind, selecting a node instead of undoing anything: the recovery
 * failing at the moment it is attempted. `hold` keeps it while the pointer is on
 * its action; `letGo` waits `TOAST_POINTER_GRACE_MS` and then lets the store's
 * answer stand. Held apart from the focus hold, so a pointer resting on one
 * toast never takes the hold off the toast a keyboard is in. Nothing moves
 * focus here, so D226's "a pointer's press never moves focus" stands.
 */
function useHeldToasts(toasts: readonly Toast[]) {
  const [held, setHeld] = useState<Toast | null>(null)
  const [pointed, setPointed] = useState<Toast | null>(null)
  const grace = useRef<number | undefined>(undefined)
  const from = useRef<HTMLElement | null>(null)
  const last = useRef<readonly Toast[]>(toasts)
  const shown = useMemo(() => {
    let out = toasts
    for (const h of [held, pointed]) {
      if (!h || out.some((t) => t.id === h.id)) continue
      const prev = last.current
      const after = new Set(prev.slice(prev.findIndex((t) => t.id === h.id) + 1).map((t) => t.id))
      const at = out.findIndex((t) => after.has(t.id))
      out = at < 0 ? [...out, h] : [...out.slice(0, at), h, ...out.slice(at)]
    }
    return out
  }, [toasts, held, pointed])
  useEffect(() => {
    last.current = shown
  })
  useEffect(() => () => window.clearTimeout(grace.current), [])

  const hold = (t: Toast) => {
    window.clearTimeout(grace.current)
    setPointed(t)
  }
  const letGo = (t: Toast) => {
    window.clearTimeout(grace.current)
    grace.current = window.setTimeout(
      () => setPointed((p) => (p?.id === t.id ? null : p)),
      TOAST_POINTER_GRACE_MS,
    )
  }

  const onFocus = (t: Toast, e: FocusEvent<HTMLElement>) => {
    if (held?.id === t.id) return
    setHeld(t)
    const came = e.relatedTarget
    from.current = came instanceof HTMLElement && !e.currentTarget.contains(came) ? came : null
  }
  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
    setHeld(null)
  }
  const release = () => {
    const back = from.current
    from.current = null
    setHeld(null)
    // Taken or put down, the toast is finished with, whichever hand held it.
    window.clearTimeout(grace.current)
    setPointed(null)
    requestAnimationFrame(() => {
      const here = document.activeElement
      if ((!here || here === document.body) && back?.isConnected)
        back.focus({ preventScroll: true })
    })
  }
  return { shown, onFocus, onBlur, release, hold, letGo }
}

export function Toasts() {
  const toasts = useStore((s) => s.toasts)
  useKeyedAnswers(toasts)
  const { shown, onFocus, onBlur, release, hold, letGo } = useHeldToasts(toasts)
  // The field's left edge, not the window's. The explorations sidebar put a
  // panel under the corner toasts had always been drawn on, so a long one began
  // on glass and finished on the field. The stylesheet keeps the 40; the inset
  // is what moves, on the same reading every other floating piece follows. Not
  // on a phone, where the sidebar is an overlay and the stack spans the width.
  const insetLeft = useStore((s) => s.viewInset.left)
  const band = useStore((s) => s.viewInset.bottom)
  const sheetUp = usePhoneSheetUp(toasts)
  // The region is mounted empty and stays mounted. It is the app's announcing
  // surface — Up next taking a press, a refusal, "Key works", a run's ending —
  // and it used to arrive WITH its first toast: a live region inserted already
  // holding its words is one a screen reader never saw change, so the first toast
  // of every quiet spell, which is nearly every toast, was never read. Empty, it
  // is a zero-height column that catches no pointer.
  const place: CSSProperties = sheetUp
    ? { top: 10, bottom: 'auto' }
    : { bottom: toastBottom(band), ...(insetLeft ? { left: insetLeft + 40 } : null) }
  return (
    <div className="toasts" role="status" aria-live="polite" style={place}>
      {shown.map((t) => (
        <div
          key={t.id}
          className="toast"
          data-toast={t.id}
          style={{ '--toast-tint': TOAST_TINT[t.tone] } as CSSProperties}
          onFocus={(e) => onFocus(t, e)}
          onBlur={onBlur}
          onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
            // Escape puts the toast down — the top layer, when focus is in it —
            // and nothing under it: App's ladder never sees this press.
            if (e.key !== 'Escape') return
            e.stopPropagation()
            useStore.getState().dismissToast(t.id)
            release()
          }}
        >
          <i />
          {t.text}
          {t.action && (
            <button
              className="toast-act"
              // The only part of the stack that takes the pointer
              // (`.toasts` is pointer-events: none), so the hold is here.
              onPointerEnter={() => hold(t)}
              onPointerLeave={() => letGo(t)}
              onClick={() => {
                // Run FIRST, and dismiss only if it did not refuse. The old order
                // dismissed unconditionally, so a "Put it back" that could not run
                // took the one route back with it — and took the sentence
                // explaining why in the same click.
                if (t.action!.run() !== false) {
                  useStore.getState().dismissToast(t.id)
                  release()
                }
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
